/* eslint-disable camelcase */
import { useCallback, useRef, createRef, useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { useSnapshot } from 'valtio';

import { updateYearIndex, updateCategoryIndex, updateMediaIndex } from '../../helpers/processEvents';
import { gmapStore, appStore, eventsStore } from '../../managers/globalState';
import globalRefs from '../../managers/globalRefs';

import Overlay from '../overlays';
import GridList from '../grid_list';
import Filter from '../filter';
import ThumbnailLoader from '../thumbnail_loader';

import EventSVG from '../../../www/assets/images/event-marker.svg';
import '../../../www/assets/css/overlays/events.css';
import '../../../www/assets/css/overlays/gmap.css';

import {
 updateYear,
 updateCategory,
 filterGmap
} from '../../helpers/processGmap';
import {
getSandMovementItems,
 getSandMovementEventId,
 getMethaneProjectGmap
} from '../../helpers/processProjects';
import { formatDateText } from '../../helpers/tools';

import { DEM_MODELS } from '../../data/dem';
import { NISAR_ITEMS } from '../../data/nisar';
import { Config } from '../../config/config';
import ar from '../../languages/ar';

/**
 * Constant vars
 */
const THUMB_WIDTH = 159;
const THUMB_HEIGHT = 102;
const ITEM_PADDING = 30;
const TITLE_TEXT = ar.Ce2s2_Projects;
const PROJECT_TABS = [ar.Earth_Deformation, ar.Sand_Movement, ar.Methane, ar.dem, ar.nisar_images];
const PROJECT_TAB_INDEX = {
 EARTH_DEFORMATION: 0,
 SAND_MOVEMENT: 1,
 METHANE: 2,
 DEM: 3,
 NISAR: 4
};
const SAND_MOVEMENT_ITEMS = getSandMovementItems();
const NISAR_THUMBNAILS = NISAR_ITEMS.map(({ external_id, list_image_src, title }) => ({
 id: external_id,
 thumbSrc: list_image_src,
 title
}));
const PROJECT_USERNAME = 'kacst';
const PROJECT_PASSWORD = 'pioneer';

export const ProjectLogin = ({ onCancel, onSuccess }) => {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');

	const handleSubmit = e => {
		e.preventDefault();

		if (username === PROJECT_USERNAME && password === PROJECT_PASSWORD) {
			onSuccess();
			return;
		}

		setError('اسم المستخدم أو كلمة المرور غير صحيحة.');
	};

	return (
		<div className='project-login-backdrop'>
			<section className='project-login-dialog' role='dialog' aria-modal='true' aria-labelledby='project-login-title'>
				<h2 id='project-login-title'>تسجيل الدخول</h2>
				<form onSubmit={handleSubmit}>
					<div className='project-login-field'>
						<label htmlFor='project-username'>اسم المستخدم</label>
						<input
							autoFocus
							autoComplete='username'
							id='project-username'
							onChange={e => {
								setUsername(e.target.value);
								setError('');
							}}
							type='text'
							value={username}
						/>
					</div>
					<div className='project-login-field'>
						<label htmlFor='project-password'>كلمة المرور</label>
						<input
							autoComplete='current-password'
							id='project-password'
							onChange={e => {
								setPassword(e.target.value);
								setError('');
							}}
							type='password'
							value={password}
						/>
					</div>
					<p className='project-login-error' role='alert'>{error}</p>
					<div className='project-login-actions'>
						<button className='sign-in' type='submit'>دخول</button>
						<button className='cancel' onClick={onCancel} type='button'>إلغاء</button>
					</div>
				</form>
			</section>
		</div>
	);
};

/**
 * Individual EventItem component to represent each event in the grid
 */
const GmapItem = props => {
 const [isHovered, setIsHovered] = useState(false);
 const { title, external_id, list_image_src, date, formatted_date, onItemClick } = props;
 const srcUrl = `${Config.baseCe2s2Url}${list_image_src}`;
 const displayDate = formatDateText( date);

 const containerStyle = {
     width: `${THUMB_WIDTH}px`,
     height: `${THUMB_HEIGHT}px`
 };
 const tipTitle = '';

 // Event handlers
 const handleMouseEnter = () => setIsHovered(true);
 const handleMouseLeave = () => setIsHovered(false);

 return (
     <figure className='event-item' onClick={() => onItemClick(external_id)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
         <div className='image-time-container' style={containerStyle}>
             <img className={`thumbnail ${isHovered ? 'zoom' : ''}`} src={srcUrl} alt={title} />
             <div className='icon-date-container'>
                 <div className='icon-group' aria-hidden='true' title={tipTitle} aria-label={tipTitle}>
                     {/* <StaticIcon /> */}
                 </div>
                 <time className='event-date' dateTime={date}>{displayDate}</time>
             </div>
         </div>
         <figcaption className='event-title'>{title}</figcaption>
     </figure>
 );
};

const ProjectThumbnailItem = ({ title, thumbSrc, id, onItemClick }) => {
 const containerStyle = {
     width: `${THUMB_WIDTH}px`,
     height: `${THUMB_HEIGHT}px`
 };

 return (
     <figure className='dem-item' onClick={() => onItemClick(id)}>
         <div className='image-time-container dem-image-container' style={containerStyle}>
             {thumbSrc ? <img className='thumbnail dem-thumbnail' src={thumbSrc} alt={title} /> : <div className='dem-thumbnail placeholder' />}
         </div>
         <figcaption className='dem-title'>{title}</figcaption>
     </figure>
 );
};

/**
 * Gmap Overlay component
 */

const GmapOverlay = ({ closeOverlay, onProjectSignOut }) => {
 const mainRef = useRef();
 const scrollableNodeRef = createRef();

 // get the global state
 const { yearIndex, categoryIndex, gmaps, gmapYears } = useSnapshot(gmapStore.state);
 const { isWindows } = useSnapshot(appStore.state);
 const { events, eventYears } = useSnapshot(eventsStore.state);
 const [projectTabIndex, setProjectTabIndex] = useState(PROJECT_TAB_INDEX.EARTH_DEFORMATION);

 // Store the category index locally as it shouldnt be set in the global state unless we View on Globe.
 const initialYearIndex = useRef(yearIndex);
 const initialCategoryIndex = useRef(categoryIndex);

 const updateProjectCategory = useCallback(e => {
     const newCategoryIndex = Number(e.currentTarget.getAttribute('data-index'));

     if (!Number.isNaN(newCategoryIndex) && newCategoryIndex !== projectTabIndex) {
         setProjectTabIndex(newCategoryIndex);
     }
 }, [projectTabIndex]);

 const filteredGmaps = gmaps ? filterGmap(gmaps, gmapYears, yearIndex, categoryIndex) : null;
 const methaneGmap = getMethaneProjectGmap(events);
 const methaneItems = methaneGmap
     ? [
         {
             id: methaneGmap.external_id,
             title: methaneGmap.title,
             thumbSrc: methaneGmap.list_image_src
         }
     ]
     : [];

 let filteredData = [];

 switch (projectTabIndex) {
     case PROJECT_TAB_INDEX.EARTH_DEFORMATION:
         filteredData = filteredGmaps;
         break;
     case PROJECT_TAB_INDEX.SAND_MOVEMENT:
         filteredData = SAND_MOVEMENT_ITEMS;
         break;
     case PROJECT_TAB_INDEX.METHANE:
         filteredData = methaneItems;
         break;
     case PROJECT_TAB_INDEX.DEM:
         filteredData = DEM_MODELS;
         break;
     case PROJECT_TAB_INDEX.NISAR:
         filteredData = NISAR_THUMBNAILS;
         break;
     default:
         filteredData = [];
 }

 const navToGmap = useCallback(eventParam => {
     closeOverlay(true);
     const { getManager } = globalRefs;
     getManager('route').navigateToGmap(eventParam);
 }, [closeOverlay]);

 const navToDemModel = useCallback(modelId => {
     closeOverlay(true);
     const { getManager } = globalRefs;
     getManager('scene').showDemModel(modelId);
 }, [closeOverlay]);

 const navToNisarImage = useCallback(imageId => {
     closeOverlay(true);
     const { getManager } = globalRefs;
     getManager('route').navigateToNisar(imageId);
 }, [closeOverlay]);

 const navToSandMovement = useCallback(itemId => {
     closeOverlay(true);
     const { getManager } = globalRefs;
     const eventId = getSandMovementEventId(itemId);
     getManager('route').navigateToSand(eventId);
 }, [closeOverlay]);

 // on mount, update the dropdowns to the current year, but default category and media to all.
 useEffect(() => {
     updateYear(yearIndex);
     updateCategory(0);
     // Find the index of the year 2024
     const yearIndexFor2024 = eventYears?.indexOf('2024');

     if (yearIndexFor2024 !== -1) {
         updateYearIndex(yearIndexFor2024);
     } else {
         console.log('Year 2024 not found in eventYears:');
     }
     updateCategoryIndex(0);
     updateMediaIndex(0);
     return () => {
         // on unmount, reset the dropdowns to the initial year and category.
         updateYear(initialYearIndex.current);
         updateCategory(initialCategoryIndex.current);
         updateYearIndex(0);
         updateCategoryIndex(0);
         updateMediaIndex(0);
     };
 }, []);

 const overlayComponents = (
     <>
         <Overlay.Header
             back
             onClose={closeOverlay}
             title={TITLE_TEXT}
             Icon={EventSVG}
         >
             <div className='events-header-content'>
                 <div className='events-header-filters'>
                     <Filter
                         type='categories'
                         allItems={PROJECT_TABS}
                         selectedIndex={projectTabIndex}
                         updateItem={updateProjectCategory}
                     />
                 </div>
                 <button className='project-sign-out' onClick={onProjectSignOut} type='button'>
                     تسجيل الخروج
                 </button>
             </div>
         </Overlay.Header>

         { filteredData === null ? <ThumbnailLoader loaderText={ar.loading_data} /> : null }
         { filteredData?.length === 0 ? <Overlay.ComingSoon /> : null }
         {
             filteredData?.length
                 ? (
                     <div className='content-mask'>
                         {projectTabIndex === PROJECT_TAB_INDEX.EARTH_DEFORMATION
                             ? (
                                 <GridList
                                     rowClassName='flex-grid-row'
                                     minPerRow={2}
                                     dataList={filteredData}
                                     itemSize={THUMB_WIDTH + ITEM_PADDING * 2}
                                     itemPadding={[ITEM_PADDING, ITEM_PADDING]}
                                     itemComponent={<GmapItem onItemClick={navToGmap} />}
                                     scrollableRef={isWindows ? scrollableNodeRef : mainRef}
                                 />
                             )
                             : null}
                         {projectTabIndex === PROJECT_TAB_INDEX.SAND_MOVEMENT
                             ? (
                                 <GridList
                                     rowClassName='flex-grid-row'
                                     minPerRow={1}
                                     maxPerRow={2}
                                     dataList={filteredData}
                                     itemSize={THUMB_WIDTH + ITEM_PADDING * 2}
                                     itemPadding={[ITEM_PADDING, ITEM_PADDING]}
                                     itemComponent={<ProjectThumbnailItem onItemClick={navToSandMovement} />}
                                     scrollableRef={isWindows ? scrollableNodeRef : mainRef}
                                 />
                             )
                             : null}
                         {projectTabIndex === PROJECT_TAB_INDEX.METHANE
                             ? (
                                 <GridList
                                     rowClassName='flex-grid-row'
                                     minPerRow={1}
                                     maxPerRow={2}
                                     dataList={filteredData}
                                     itemSize={THUMB_WIDTH + ITEM_PADDING * 2}
                                     itemPadding={[ITEM_PADDING, ITEM_PADDING]}
                                     itemComponent={<ProjectThumbnailItem onItemClick={navToGmap} />}
                                     scrollableRef={isWindows ? scrollableNodeRef : mainRef}
                                 />
                             )
                             : null}
                         {projectTabIndex === PROJECT_TAB_INDEX.DEM
                             ? (
                                 <GridList
                                     rowClassName='flex-grid-row'
                                     minPerRow={2}
                                     dataList={filteredData}
                                     itemSize={THUMB_WIDTH + ITEM_PADDING * 2}
                                     itemPadding={[ITEM_PADDING, ITEM_PADDING]}
                                     itemComponent={<ProjectThumbnailItem onItemClick={navToDemModel} />}
                                     scrollableRef={isWindows ? scrollableNodeRef : mainRef}
                                 />
                             )
                             : null}
                         {projectTabIndex === PROJECT_TAB_INDEX.NISAR
                             ? (
                                 <GridList
                                     rowClassName='flex-grid-row'
                                     minPerRow={2}
                                     dataList={filteredData}
                                     itemSize={THUMB_WIDTH + ITEM_PADDING * 2}
                                     itemPadding={[ITEM_PADDING, ITEM_PADDING]}
                                     itemComponent={<ProjectThumbnailItem onItemClick={navToNisarImage} />}
                                     scrollableRef={isWindows ? scrollableNodeRef : mainRef}
                                 />
                             )
                             : null}
                     </div>
                 )
                 : null
         }

     </>
 );

 return (
     <Overlay type='events' ref={mainRef}>
         {isWindows
             ? (
                 <SimpleBar scrollableNodeProps={{ ref: scrollableNodeRef }} style={{ height: '100%' }}>
                     {overlayComponents}
                 </SimpleBar>
             )
             : overlayComponents}
     </Overlay>
 );
};

export default GmapOverlay;
